import React, { useState } from 'react';
import { ArrowLeftIcon, ScaleIcon, PrinterIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, ClipboardListIcon, ChevronRightIcon, PackageIcon, DropletIcon, InfoIcon } from 'lucide-react';
import { WeighingItem } from './FlourOilWeighing';
interface FlourOilWeighingProcessProps {
  item: WeighingItem;
  onWeighingComplete: () => void;
  onBack: () => void;
}
export function FlourOilWeighingProcess({
  item,
  onWeighingComplete,
  onBack
}: FlourOilWeighingProcessProps) {
  const [weight, setWeight] = useState<number | null>(null);
  const [isWeighing, setIsWeighing] = useState(false);
  const [isWithinTolerance, setIsWithinTolerance] = useState<boolean | null>(null);
  const [isPrinted, setIsPrinted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [cumulativeWeight, setCumulativeWeight] = useState(0);
  // Für die Lot-Informationen
  const lotSize = 5;
  const boxesWeighed = 2;
  const lotTotalWeight = item.quantity * lotSize;
  const lotWeighedSoFar = item.quantity * boxesWeighed;
  const tolerance = item.quantity * 0.02; // 2% Toleranz
  const startWeighing = () => {
    setIsWeighing(true);
    // Simuliere den Wiegeprozess
    setTimeout(() => {
      // Zufälliges Gewicht innerhalb oder außerhalb der Toleranz
      const randomWeight = item.quantity + (Math.random() * (tolerance * 1.2) - tolerance * 0.6);
      setWeight(parseFloat(randomWeight.toFixed(2)));
      setIsWeighing(false);
      // Aktualisiere das kumulative Gewicht
      const newCumulativeWeight = cumulativeWeight + randomWeight;
      setCumulativeWeight(newCumulativeWeight);
      // Prüfe, ob innerhalb der Toleranz
      const withinTolerance = Math.abs(randomWeight - item.quantity) <= tolerance;
      setIsWithinTolerance(withinTolerance);
    }, 2000);
  };
  const printLabel = () => {
    // Simuliere Druckvorgang
    setIsPrinted(true);
    // Zeige Erfolgsmeldung nach dem Druck
    setTimeout(() => {
      setShowSuccess(true);
      // Nach kurzer Zeit zurück zum nächsten Material
      setTimeout(() => {
        onWeighingComplete();
      }, 2000);
    }, 1000);
  };
  const getWeightStatus = () => {
    if (isWithinTolerance === null) return '';
    return isWithinTolerance ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
  };
  // Berechne den Fortschritt der Gesamtverwiegung des Lots
  const lotWeightProgress = (lotWeighedSoFar + (weight || 0)) / lotTotalWeight * 100;
  return <div className="container mx-auto" data-id="element-371">
      {showSuccess ? <div className="bg-white rounded-xl shadow-md p-8 text-center" data-id="element-372">
          <div className="bg-green-100 p-3 rounded-full inline-flex mb-4" data-id="element-373">
            <CheckCircleIcon size={48} className="text-green-600" data-id="element-374" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-375">
            Verwiegung erfolgreich!
          </h3>
          <p className="text-gray-600 mb-2" data-id="element-376">
            Etikett wurde gedruckt und Material erfolgreich verarbeitet.
          </p>
          <p className="text-sm text-gray-500" data-id="element-377">
            Sie werden automatisch weitergeleitet...
          </p>
        </div> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-378">
          <div className="lg:col-span-2" data-id="element-379">
            <div className="bg-white rounded-xl shadow-md p-5" data-id="element-380">
              {/* Kompakte Material-Info */}
              <div className="flex items-center justify-between mb-4" data-id="element-381">
                <div data-id="element-382">
                  <h3 className="text-lg font-semibold text-gray-800" data-id="element-383">
                    {item.name}{' '}
                    <span className="text-sm font-normal text-gray-500" data-id="element-384">
                      ({item.id})
                    </span>
                  </h3>
                  <div className="flex items-center text-sm text-gray-600" data-id="element-385">
                    {item.type === 'oil' ? <DropletIcon size={16} className="text-amber-700 mr-1" data-id="element-386" /> : <div size={16} className="text-blue-700 mr-1" data-id="element-387" />}
                    <span data-id="element-388">
                      {item.type === 'oil' ? 'Ölkomponente' : 'Mehlkomponente'}
                    </span>
                  </div>
                </div>
                <div className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full" data-id="element-389">
                  Box {boxesWeighed + 1}/{lotSize}
                </div>
              </div>
              {/* Lot-Fortschritt */}
              <div className="mb-4 bg-gray-50 p-3 rounded-lg" data-id="element-390">
                <div className="flex justify-between items-center mb-1 text-sm" data-id="element-391">
                  <span className="font-medium text-gray-700" data-id="element-392">
                    Lot-Fortschritt:
                  </span>
                  <span className="font-medium text-gray-700" data-id="element-393">
                    {(lotWeighedSoFar + (weight || 0)).toFixed(1)}/
                    {lotTotalWeight} {item.unit}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-1" data-id="element-394">
                  <div className="bg-[#8DC63F] h-2 rounded-full transition-all duration-500" style={{
                width: `${Math.min(lotWeightProgress, 100)}%`
              }} data-id="element-395"></div>
                </div>
              </div>
              {/* Wiegebereich */}
              <div className={`border rounded-lg p-5 mb-4 ${getWeightStatus()}`} data-id="element-396">
                <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-397">
                  {/* Wiegeanzeige */}
                  <div className="flex-shrink-0 text-center" data-id="element-398">
                    <div className={`
                      p-4 rounded-lg inline-block 
                      ${!weight ? 'bg-gray-100' : isWithinTolerance ? 'bg-green-100' : 'bg-red-100'}
                    `} data-id="element-399">
                      {!weight ? <ScaleIcon size={40} className="text-gray-500 mx-auto" data-id="element-400" /> : isWithinTolerance ? <CheckCircleIcon size={40} className="text-green-600 mx-auto" data-id="element-401" /> : <XCircleIcon size={40} className="text-red-600 mx-auto" data-id="element-402" />}
                      <div className="text-2xl font-bold mt-2" data-id="element-403">
                        {weight ? weight : '--'} {item.unit}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-404">
                        Ziel: {item.quantity} ±{tolerance.toFixed(2)}{' '}
                        {item.unit}
                      </div>
                    </div>
                  </div>
                  {/* Anweisungen und Aktionen */}
                  <div className="flex-1" data-id="element-405">
                    {!weight ? <div data-id="element-406">
                        <p className="text-sm text-gray-600 mb-3" data-id="element-407">
                          {item.type === 'oil' ? 'Platzieren Sie den Behälter auf der Waage und füllen Sie das Öl ein.' : 'Platzieren Sie den Behälter auf der Waage und füllen Sie das Mehl ein.'}
                        </p>
                        <button onClick={startWeighing} disabled={isWeighing} className={`
                          bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg 
                          text-sm font-medium transition-colors flex items-center gap-2
                          ${isWeighing ? 'opacity-70 cursor-not-allowed' : ''}
                        `} data-id="element-408">
                          <ScaleIcon size={16} data-id="element-409" />
                          <span data-id="element-410">
                            {isWeighing ? 'Wiegen...' : 'Wiegen starten'}
                          </span>
                        </button>
                      </div> : isWithinTolerance ? <div data-id="element-411">
                        <p className="text-sm text-green-600 mb-3" data-id="element-412">
                          {boxesWeighed + 1 === lotSize ? 'Das Lot wurde vollständig innerhalb der Toleranz verwogen.' : 'Die Box wurde erfolgreich verwogen.'}
                        </p>
                        <button onClick={printLabel} disabled={isPrinted} className={`
                          bg-[#8DC63F] hover:bg-[#7AB929] text-white px-4 py-2 rounded-lg 
                          text-sm font-medium transition-colors flex items-center gap-2
                          ${isPrinted ? 'opacity-70 cursor-not-allowed' : ''}
                        `} data-id="element-413">
                          <PrinterIcon size={16} data-id="element-414" />
                          <span data-id="element-415">
                            {isPrinted ? 'Drucken...' : 'Etikett drucken'}
                          </span>
                        </button>
                      </div> : <div data-id="element-416">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3" data-id="element-417">
                          <p className="text-sm text-red-600 flex items-start" data-id="element-418">
                            <AlertTriangleIcon size={16} className="mr-1 flex-shrink-0 mt-0.5" data-id="element-419" />
                            <span data-id="element-420">
                              Gewicht außerhalb der Toleranz! Bitte korrigieren
                              Sie die Menge und wiegen Sie erneut.
                            </span>
                          </p>
                        </div>
                        <button onClick={startWeighing} disabled={isWeighing} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-421">
                          <ScaleIcon size={16} data-id="element-422" />
                          <span data-id="element-423">Erneut wiegen</span>
                        </button>
                      </div>}
                  </div>
                </div>
              </div>
              {/* Etikett-Vorschau */}
              {weight && isWithinTolerance && <div className="border border-gray-200 p-3 rounded-lg" data-id="element-424">
                  <div className="flex items-center justify-between mb-2" data-id="element-425">
                    <h4 className="text-sm font-medium text-gray-700" data-id="element-426">
                      Etikett-Vorschau
                    </h4>
                    <span className="text-xs text-gray-500" data-id="element-427">
                      Wird nach Bestätigung gedruckt
                    </span>
                  </div>
                  <div className="border border-gray-200 p-3 rounded-md bg-white text-sm" data-id="element-428">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1" data-id="element-429">
                      <div className="text-gray-600" data-id="element-430">Material:</div>
                      <div className="font-medium" data-id="element-431">{item.name}</div>
                      <div className="text-gray-600" data-id="element-432">Artikel-Nr:</div>
                      <div className="font-medium" data-id="element-433">{item.id}</div>
                      <div className="text-gray-600" data-id="element-434">Box:</div>
                      <div className="font-medium" data-id="element-435">
                        {boxesWeighed + 1}/{lotSize}
                      </div>
                      <div className="text-gray-600" data-id="element-436">Gewicht:</div>
                      <div className="font-medium" data-id="element-437">
                        {weight} {item.unit}
                      </div>
                      <div className="text-gray-600" data-id="element-438">Datum/Zeit:</div>
                      <div className="font-medium" data-id="element-439">
                        {new Date().toLocaleDateString()}{' '}
                        {new Date().toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
          {/* Seitenleiste */}
          <div className="lg:col-span-1" data-id="element-440">
            <div className="bg-white rounded-xl shadow-md p-4" data-id="element-441">
              {/* Lot-Informationen */}
              <div className="mb-4" data-id="element-442">
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center" data-id="element-443">
                  <PackageIcon size={16} className="mr-1" data-id="element-444" />
                  <span data-id="element-445">
                    Lot: {item.type === 'oil' ? 'OIL-' : 'FLOUR-'}
                    {new Date().getFullYear()}-
                    {Math.floor(Math.random() * 1000)}
                  </span>
                </h4>
                <div className="bg-blue-50 p-3 rounded-lg text-sm" data-id="element-446">
                  <div className="grid grid-cols-2 gap-1" data-id="element-447">
                    <div className="text-gray-600" data-id="element-448">Gesamtgewicht:</div>
                    <div className="font-medium text-right" data-id="element-449">
                      {lotTotalWeight} {item.unit}
                    </div>
                    <div className="text-gray-600" data-id="element-450">Bisher verwogen:</div>
                    <div className="font-medium text-right" data-id="element-451">
                      {lotWeighedSoFar} {item.unit}
                    </div>
                    <div className="text-gray-600" data-id="element-452">Boxen:</div>
                    <div className="font-medium text-right" data-id="element-453">
                      {boxesWeighed}/{lotSize}
                    </div>
                  </div>
                  <div className="mt-2" data-id="element-454">
                    <div className="w-full bg-blue-200 rounded-full h-1.5 mb-1" data-id="element-455">
                      <div className="bg-blue-600 h-1.5 rounded-full" style={{
                    width: `${boxesWeighed / lotSize * 100}%`
                  }} data-id="element-456"></div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Hinweis */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3" data-id="element-457">
                <p className="text-xs text-yellow-700 flex items-start" data-id="element-458">
                  <AlertTriangleIcon size={14} className="mr-1 flex-shrink-0 mt-0.5" data-id="element-459" />
                  <span data-id="element-460">
                    {item.type === 'oil' ? 'Bei Ölkomponenten ist besonders auf die Viskosität zu achten. Gießen Sie langsam, um Überläufe zu vermeiden.' : 'Bei Mehlkomponenten ist auf Staubbildung zu achten. Verwenden Sie bei Bedarf eine Absaugung.'}
                  </span>
                </p>
              </div>
              {/* Zurück-Button */}
              <button onClick={onBack} className="w-full mt-4 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded flex items-center justify-center gap-2" data-id="element-461">
                <ArrowLeftIcon size={16} data-id="element-462" />
                <span data-id="element-463">Zurück zur Materialauswahl</span>
              </button>
            </div>
          </div>
        </div>}
    </div>;
}